import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react"
import { KEYS } from "@/constants/ui"
import { ClientRateLimitException } from "@/types/client-exceptions"
import {
  Location,
  LocationCategoryTier,
  LocationSearchHit,
} from "@/types/place"

import { locationToAmplitudeProperties } from "@/lib/analytics/amplitude-properties"
import {
  sendAnalyticsEvent,
  setAnalyticsUserProperties,
} from "@/lib/analytics/amplitude-utils"
import { httpIsCancelled } from "@/lib/exception-utils"
import { fullLocationName, unambiguousLocationName } from "@/lib/location-utils"
import { isSerializedClientError } from "@/lib/storage/api/base-query"
import { useLazySearchLocationsQuery } from "@/lib/storage/api/location-api"
import { placeApi, useLazyGetLocationQuery } from "@/lib/storage/api/place-api"
import { selectOrganization } from "@/lib/storage/app-slice"
import { showRegisterGateDialog } from "@/lib/storage/auth-thunks"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { EntityIcon } from "./entity-icon"
import {
  Autocomplete,
  AutocompleteContent,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
  AutocompleteStatus,
} from "./ui/autocomplete"
import { ErrorMessage } from "./ui/error-message"
import type { InputGroupInputProps, InputGroupSize } from "./ui/input-group"
import { Loading } from "./ui/loading"
import { Photo } from "./ui/photo"

export const LOCATION_INPUT_MIN_QUERY_LENGTH = 2
const LOCATION_SEARCH_DEBOUNCE_MS = 150
const SEARCH_LOADING_DELAY_MS = 300

interface LocationInputProps extends Pick<
  InputGroupInputProps,
  | "type"
  | "autoComplete"
  | "autoFocus"
  | "disabled"
  | "readOnly"
  | "placeholder"
  | "required"
  | "id"
  | "name"
> {
  location?: Location | LocationSearchHit | string
  onLocationSelected: (location?: Location) => void
  categoryTier?: LocationCategoryTier | LocationCategoryTier[]
  useFormControl?: boolean
  onInputChange?: (value: string) => void
  popoverSize?: "anchor" | "auto" | "sm" | "md" | "lg"
  usePopover?: boolean
  size?: InputGroupSize
  muted?: boolean
  className?: string
}

function categoryTiers(
  categoryTier?: LocationCategoryTier | LocationCategoryTier[]
) {
  return Array.isArray(categoryTier)
    ? categoryTier
    : categoryTier
      ? [categoryTier]
      : []
}

function notFoundMessage(
  categoryTier?: LocationCategoryTier | LocationCategoryTier[]
) {
  const tiers = categoryTiers(categoryTier)
  if (
    tiers.length === 2 &&
    tiers.includes("city_tier") &&
    tiers.includes("neighborhood_tier")
  ) {
    return "Sorry, we couldn’t find that city or neighborhood."
  }
  if (
    tiers.length === 3 &&
    tiers.includes("city_tier") &&
    tiers.includes("neighborhood_tier") &&
    tiers.includes("region_tier")
  ) {
    return "Sorry, we couldn’t find that city, neighborhood, or region."
  }
  if (
    tiers.length === 4 &&
    tiers.includes("city_tier") &&
    tiers.includes("neighborhood_tier") &&
    tiers.includes("region_tier") &&
    tiers.includes("country_tier")
  ) {
    return "Sorry, we couldn’t find that place."
  }

  if (tiers.length !== 1) {
    return "Sorry, we couldn’t find that location."
  }

  switch (tiers[0]) {
    case "country_tier":
      return "Sorry, we couldn’t find that country."
    case "city_tier":
      return "Sorry, we couldn’t find that city."
    case "neighborhood_tier":
      return "Sorry, we couldn’t find that neighborhood."
    case "region_tier":
      return "Sorry, we couldn’t find that region."
    case "continent_tier":
      return "Sorry, we couldn’t find that continent."
    default:
      return "Sorry, we couldn’t find that location."
  }
}

export function LocationInput({
  location: displayLocation,
  onLocationSelected,
  categoryTier,
  onInputChange,
  useFormControl,
  autoFocus,
  popoverSize = "auto",
  usePopover = true,
  size = "md",
  muted,
  className,
  type,
  autoComplete,
  disabled,
  readOnly,
  placeholder,
  required,
  id,
  name,
}: LocationInputProps) {
  const dispatch = useAppDispatch()
  const organization = useAppSelector(selectOrganization)
  const [triggerLocationQuery] = useLazyGetLocationQuery()
  const [triggerSearchLocations] = useLazySearchLocationsQuery()

  // hack to allow string default value (to avoid extra server call to get full Location object)
  const location =
    typeof displayLocation === "string" ? undefined : displayLocation

  const [newLocationName, setNewLocationName] = useState<string>(
    typeof displayLocation === "string"
      ? displayLocation
      : location
        ? fullLocationName(location)
        : ""
  )
  const [availableHits, setAvailableHits] = useState<LocationSearchHit[]>([])
  const [selectedLocation, setSelectedLocation] = useState<
    Location | LocationSearchHit | undefined
  >(location)
  const [showError, setShowError] = useState(false)
  const [highlightedText, setHighlightedText] = useState("")
  const [query, setQuery] = useState("")
  const [showLoading, setShowLoading] = useState(false)

  const debouncedSearchRef = useRef<NodeJS.Timeout | null>(null)
  const loadingTimerRef = useRef<NodeJS.Timeout | null>(null)
  const inFlightSearchRequestRef = useRef<ReturnType<
    typeof triggerSearchLocations
  > | null>(null)
  const isSelectingRef = useRef(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const stopLoadingIndicator = () => {
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current)
      loadingTimerRef.current = null
    }
    setShowLoading(false)
  }

  const isOpen =
    (query.length >= LOCATION_INPUT_MIN_QUERY_LENGTH &&
      (availableHits.length > 0 || showLoading)) ||
    showError

  const [prevLocation, setPrevLocation] = useState(location)
  // Reset draft state when the parent-provided location changes. Adjust during
  // render instead of syncing in an effect.
  if (location !== prevLocation) {
    setPrevLocation(location)
    if (location) {
      setNewLocationName(fullLocationName(location))
      setSelectedLocation(location)
    } else {
      setNewLocationName("")
      setSelectedLocation(undefined)
    }
  }

  const searchLocations = async (searchQuery: string) => {
    if (searchQuery.length < LOCATION_INPUT_MIN_QUERY_LENGTH) {
      stopLoadingIndicator()
      setAvailableHits([])
      setShowError(false)
      return
    }

    if (
      selectedLocation &&
      searchQuery === fullLocationName(selectedLocation)
    ) {
      stopLoadingIndicator()
      return
    }

    inFlightSearchRequestRef.current?.abort()
    const request = triggerSearchLocations(
      {
        query: searchQuery,
        limit: 5,
        relativeToLocation: selectedLocation?.entity_ref,
        categoryTier,
      },
      true
    )
    inFlightSearchRequestRef.current = request

    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current)
    }
    loadingTimerRef.current = setTimeout(() => {
      setShowLoading(true)
    }, SEARCH_LOADING_DELAY_MS)

    try {
      const result = await request.unwrap()
      stopLoadingIndicator()
      setAvailableHits(result.hits)
      if (result.hits.length === 0) {
        setShowError(true)
      } else {
        setShowError(false)
      }
    } catch (error) {
      // A cancelled request means a newer search superseded this one; that
      // search owns the loading indicator now.
      if (httpIsCancelled(error)) {
        return
      }
      stopLoadingIndicator()
      const isClassRateLimitError = error instanceof ClientRateLimitException
      const isSerializedRateLimitError = isSerializedClientError(
        error,
        "ClientRateLimitException"
      )
      const reason = isClassRateLimitError
        ? error.data.reason
        : isSerializedRateLimitError &&
            error.data &&
            typeof error.data === "object" &&
            "reason" in error.data
          ? error.data.reason
          : undefined

      if (reason === "anon_throttling" || reason === "unverified_throttling") {
        setAnalyticsUserProperties({
          type: "rateLimitProps",
          rateLimit: { reason },
        })
        void dispatch(showRegisterGateDialog("location-search"))
      } else {
        console.error("Failed to search locations: ", error)
        setShowError(true)
      }
    }
  }

  // The debounce timeout fires later; read the latest closure through a ref so
  // it sees current props/state instead of the render it was scheduled in.
  const searchLocationsRef = useRef(searchLocations)
  searchLocationsRef.current = searchLocations

  useEffect(() => {
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current)
    }

    debouncedSearchRef.current = setTimeout(() => {
      void searchLocationsRef.current(query)
    }, LOCATION_SEARCH_DEBOUNCE_MS)

    return () => {
      if (debouncedSearchRef.current) {
        clearTimeout(debouncedSearchRef.current)
      }
      inFlightSearchRequestRef.current?.abort()
    }
  }, [query])

  const selectHit = async (hit: LocationSearchHit) => {
    isSelectingRef.current = true
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current)
      debouncedSearchRef.current = null
    }

    const hitFullName = fullLocationName(hit)
    stopLoadingIndicator()
    setQuery("")
    setNewLocationName(hitFullName)
    setAvailableHits([])
    setShowError(false)
    try {
      const locationSummary = await triggerLocationQuery(
        hit.entity_ref,
        true
      ).unwrap()
      const fullName = fullLocationName(locationSummary)
      setSelectedLocation(locationSummary)
      onLocationSelected(locationSummary)
      setNewLocationName(fullName)
      sendAnalyticsEvent({
        name: "LocationSelect",
        properties: {
          ...locationToAmplitudeProperties(locationSummary),
        },
      })
    } catch (error) {
      console.error("Failed to fetch location details:", error)
      setShowError(true)
    } finally {
      isSelectingRef.current = false
    }
  }

  const handleClear = () => {
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current)
      debouncedSearchRef.current = null
    }
    stopLoadingIndicator()
    setQuery("")
    setAvailableHits([])
    setShowError(false)
    setNewLocationName("")
    setSelectedLocation(undefined)
    onLocationSelected(undefined)
    onInputChange?.("")
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KEYS.ESC && newLocationName) {
      e.stopPropagation()
      handleClear()
    }
  }

  const trimmedHighlight = highlightedText.trim().toLowerCase()
  const highlightPattern = trimmedHighlight
    ? {
        // https://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex
        regex: new RegExp(
          `(${trimmedHighlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
          "gi"
        ),
        trimmed: trimmedHighlight,
      }
    : null

  const highlight = (locationHit: LocationSearchHit) => {
    const fullName = unambiguousLocationName(locationHit, availableHits)
    if (!highlightPattern) {
      return <span>{fullName}</span>
    }

    const parts = fullName.split(highlightPattern.regex)

    return (
      <span>
        {parts.map((part, index) =>
          part.toLowerCase().trim() === highlightPattern.trimmed ? (
            <span key={index} className="font-semibold">
              {part}
            </span>
          ) : (
            part
          )
        )}
      </span>
    )
  }

  const statusContent: ReactNode = showError ? (
    <ErrorMessage className="my-2.5 text-center text-sm">
      {organization?.geo_name
        ? `Sorry, cannot search outside of ${organization.geo_name}.`
        : notFoundMessage(categoryTier)}
    </ErrorMessage>
  ) : showLoading ? (
    <div
      aria-label="Loading locations"
      className="flex h-65 items-center justify-center"
    >
      <Loading />
    </div>
  ) : null

  const resultsList = (
    <AutocompleteList
      aria-label="Locations"
      className={!usePopover ? "mt-2 max-h-none px-0 py-0" : undefined}
    >
      {(locationHit: LocationSearchHit) => (
        <AutocompleteItem
          key={locationHit.entity_ref}
          value={locationHit}
          onClick={() => {
            void selectHit(locationHit)
          }}
          className="no-translate gap-2.5 rounded-xl p-1.5 text-sm leading-tight"
        >
          <div className="relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-3">
            {locationHit.photo ? (
              <Photo
                src={locationHit.photo}
                className="absolute inset-0 size-full"
                width="sm"
                alt=""
              />
            ) : (
              <EntityIcon type="location" size={16} />
            )}
          </div>
          <div className="line-clamp-2 text-pretty">
            <span>{highlight(locationHit)}</span>
          </div>
        </AutocompleteItem>
      )}
    </AutocompleteList>
  )

  return (
    <Autocomplete
      items={availableHits}
      value={newLocationName}
      filter={null}
      autoHighlight
      inline={!usePopover}
      open={isOpen}
      itemToStringValue={(hit) => fullLocationName(hit)}
      onOpenChange={(open) => {
        if (!open) {
          setTimeout(() => {
            setAvailableHits([])
          }, 150)
        }
      }}
      onItemHighlighted={(hit) => {
        if (hit) {
          void dispatch(
            placeApi.util.prefetch("getLocation", hit.entity_ref, {
              force: false,
            })
          )
        }
      }}
      onValueChange={(nextValue, details) => {
        if (details.reason === "item-press") {
          return
        }

        if (
          details.reason === "clear-press" ||
          details.reason === "input-clear"
        ) {
          handleClear()
          return
        }

        setQuery(nextValue)
        setHighlightedText(nextValue)
        setNewLocationName(nextValue)
        onInputChange?.(nextValue)
      }}
    >
      <AutocompleteInput
        ref={inputRef}
        type={type}
        size={size}
        muted={muted}
        className={className}
        useFormControl={useFormControl}
        autoFocus={autoFocus}
        onFocus={(e) => {
          if (selectedLocation) {
            e.target.select()
          }
        }}
        onBlur={() => {
          if (isSelectingRef.current || !usePopover) {
            return
          }
          if (selectedLocation) {
            // Revert to selected location if text was changed
            const selectedName = fullLocationName(selectedLocation)
            if (newLocationName !== selectedName) {
              setNewLocationName(selectedName)
              setQuery(selectedName)
            }
          } else if (newLocationName) {
            // No selection made, clear the input
            setNewLocationName("")
            setQuery("")
            setShowError(false)
          }
        }}
        onKeyDown={handleKeyDown}
        autoComplete={autoComplete ?? "off"}
        autoCorrect="off"
        data-1p-ignore
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        required={required}
        id={id}
        name={name}
      />

      {usePopover ? (
        <AutocompleteContent size={popoverSize} className="px-2 py-2.5">
          {statusContent && (
            <AutocompleteStatus>{statusContent}</AutocompleteStatus>
          )}
          {!showLoading && resultsList}
        </AutocompleteContent>
      ) : (
        <>
          {statusContent && isOpen && (
            <AutocompleteStatus>{statusContent}</AutocompleteStatus>
          )}
          {isOpen && !showLoading && resultsList}
        </>
      )}
    </Autocomplete>
  )
}
